import styles from "@/components/Bottombar/index.module.less";
import cs from "classnames";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import BadgeUI from "@/componentsUI/Badge";
import { IconQb } from "@/assets/svg-icon";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import { useLocation, useNavigate } from "react-router";
import {
  useGlobalOpenSearch,
  useGlobalOpenUserCenter,
  useGlobalOpenWallet,
  useGlobalState,
  useMobileLeftSiderSwitch,
} from "@/store/commonStore";
import useDialogManager from "@/utils/useDialogManager";

// 导入图标
import menuIcon from "./assets/menu.webp";
import homeIcon from "./assets/home.webp";
import withdrawIcon from "./assets/withdraw.webp";
import earnIcon from "./assets/earn.webp";

const WalletTabIndex = {
  Deposit: 0,
  Withdraw: 2,
} as const;

// Bottom bar索引类型（-1 表示无激活状态）
type BarIndex = -1 | 0 | 1 | 2 | 3 | 4;

// 定义图标组件的props类型
interface IconProps {
  isActive: boolean;
}

// Bottom bar配置类型
interface BottombarConfig {
  title: string;
  middleRadius: boolean;
  icon: React.ReactNode | ((isActive: boolean) => React.ReactNode);
  route?: string;
  requiresAuth?: boolean;
}

// 使用React.memo优化图标组件，避免不必要的重渲染
const MenuIcon = React.memo(
  ({ isActive, showRedDot }: IconProps & { showRedDot?: boolean }) => (
    <div className={styles["bar-icon"]}>
      <BadgeUI
        count={showRedDot ? 1 : 0}
        dot
        className={styles["menu-badge"]}
      >
        <img
          src={menuIcon}
          alt="menu"
          width="22"
          height="22"
          className={isActive ? styles["active-icon"] : ""}
        />
      </BadgeUI>
    </div>
  )
);

const HomeIcon = React.memo(({ isActive }: IconProps) => (
  <div className={styles["bar-icon"]}>
    <img
      src={homeIcon}
      alt="home"
      width="22"
      height="22"
      className={isActive ? styles["active-icon"] : ""}
    />
  </div>
));

const WithdrawIcon = React.memo(({ isActive }: IconProps) => (
  <div className={styles["bar-icon"]}>
    <img
      src={withdrawIcon}
      alt="withdraw"
      width="22"
      height="22"
      className={isActive ? styles["active-icon"] : ""}
    />
  </div>
));

const EarnIcon = React.memo(({ isActive }: IconProps) => (
  <div className={styles["bar-icon"]}>
    <img
      src={earnIcon}
      alt="earn"
      width="22"
      height="22"
      className={isActive ? styles["active-icon"] : ""}
    />
  </div>
));

function Bottombar() {
  const navigate = useNavigate();
  const { isLogin } = useGlobalState();
  const { openSignIn, openWallet } = useDialogManager();

  // Bottom bar激活索引
  const [activeIndex, setActiveIndex] = useState<BarIndex>(3);
  const [drawerVisible, setDrawerVisible] = useMobileLeftSiderSwitch();
  const [searchVisible, setSearchVisible] = useGlobalOpenSearch();
  const [walletSwitch, openWalletSwitch] = useGlobalOpenWallet();
  const [visible, setVisible] = useGlobalOpenUserCenter();
  const [lastWalletBarClicked, setLastWalletBarClicked] = useState<BarIndex>(2);
  const [showMenuRedDot, setShowMenuRedDot] = useState(false);

  const { pathname } = useLocation();
  const { t } = useTranslationPlus("HomeView");

  // 使用 ref 避免闭包问题，不触发重新渲染
  const isNavigatingRef = useRef(false);
  const activeIndexRef = useRef<BarIndex>(activeIndex);
  const pathnameRef = useRef(pathname);

  // 同步 ref
  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  // 检查是否显示红点
  useEffect(() => {
    try {
      const lastClickDate = localStorage.getItem("menuLastClickDate");
      const today = new Date().toDateString();
      setShowMenuRedDot(!lastClickDate || lastClickDate !== today);
    } catch (error) {
      console.error("Error checking red dot:", error);
    }
  }, []);

  // 震动反馈工具函数
  const triggerHapticFeedback = useCallback(() => {
    try {
      if ("vibrate" in navigator) {
        navigator.vibrate(10);
      }
    } catch (error) {
      // 忽略震动API错误
    }
  }, []);

  // 移动端底部导航点击处理函数 - 使用 ref 大幅减少依赖项
  const onClickItem = useCallback(
    (index: BarIndex) => {
      // 使用 ref 检查，避免状态更新导致的重新渲染
      if (isNavigatingRef.current) {
        return;
      }

      try {
        triggerHapticFeedback();
        isNavigatingRef.current = true;

        // 100ms 后重置导航状态，不触发组件重新渲染
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);

        // 立即更新 activeIndex，避免闪烁
        setActiveIndex(index);

        switch (index) {
          case 0:
            if (showMenuRedDot) {
              try {
                localStorage.setItem(
                  "menuLastClickDate",
                  new Date().toDateString()
                );
                setShowMenuRedDot(false);
              } catch (error) {
                console.error("Error saving menu click date:", error);
              }
            }
            setDrawerVisible(!drawerVisible);
            openWalletSwitch(false);
            setSearchVisible(false);
            setVisible(false);
            break;

          case 1:
            setDrawerVisible(false);
            openWalletSwitch(false);
            setSearchVisible(false);
            setVisible(false);
            if (!pathnameRef.current.includes("home")) {
              navigate("/home");
            }
            break;

          case 2:
            setLastWalletBarClicked(2);
            void openWallet({ tabIndex: WalletTabIndex.Deposit });
            setDrawerVisible(false);
            setSearchVisible(false);
            setVisible(false);
            break;

          case 3:
            setLastWalletBarClicked(3);
            void openWallet({ tabIndex: WalletTabIndex.Withdraw });
            setDrawerVisible(false);
            setSearchVisible(false);
            setVisible(false);
            break;

          case 4:
            setDrawerVisible(false);
            openWalletSwitch(false);
            setSearchVisible(false);
            setVisible(false);
            if (isLogin) {
              if (!pathnameRef.current.includes("NationalProxy")) {
                navigate("/NationalProxy");
              }
            } else {
              openSignIn();
            }
            break;

          default:
            setDrawerVisible(false);
            openWalletSwitch(false);
            setSearchVisible(false);
            setVisible(false);
            break;
        }
      } catch (error) {
        console.error("Bottom bar navigation error:", error);
        isNavigatingRef.current = false;
      }
    },
    [
      triggerHapticFeedback,
      showMenuRedDot,
      drawerVisible,
      isLogin,
      setDrawerVisible,
      openWalletSwitch,
      setSearchVisible,
      setVisible,
      navigate,
      openSignIn,
      openWallet,
    ]
  );

  // 使用useMemo缓存底部导航数据，避免重复创建
  const items = useMemo<BottombarConfig[]>(
    () => [
      {
        title: t("tabMenu"),
        middleRadius: false,
        icon: (isActive: boolean) => (
          <MenuIcon isActive={isActive} showRedDot={showMenuRedDot} />
        ),
        route: undefined,
        requiresAuth: false,
      },
      {
        title: t("tabHome"),
        middleRadius: false,
        icon: (isActive: boolean) => <HomeIcon isActive={isActive} />,
        route: "/home",
        requiresAuth: false,
      },
      {
        title: t("tabDeposit"),
        middleRadius: true,
        icon: <IconQb className={""} />,
        route: undefined,
        requiresAuth: true,
      },
      {
        title: t("tabWithdraw"),
        middleRadius: false,
        icon: (isActive: boolean) => <WithdrawIcon isActive={isActive} />,
        route: undefined,
        requiresAuth: true,
      },
      {
        title: t("Affiliate"),
        middleRadius: false,
        icon: (isActive: boolean) => <EarnIcon isActive={isActive} />,
        route: "/NationalProxy",
        requiresAuth: true,
      },
    ],
    [t, showMenuRedDot]
  );

  // 路径同步 - 只在路径变化且不是用户点击导致时同步
  useEffect(() => {
    // 如果正在导航中，跳过同步
    if (isNavigatingRef.current) {
      return;
    }

    if (drawerVisible) {
      if (activeIndexRef.current !== 0) {
        setActiveIndex(0);
      }
      return;
    }

    if (walletSwitch) {
      if (activeIndexRef.current !== lastWalletBarClicked) {
        setActiveIndex(lastWalletBarClicked);
      }
      return;
    }

    if (searchVisible) {
      if (activeIndexRef.current !== 3) {
        setActiveIndex(3);
      }
      return;
    }

    // 路径映射
    if (pathname.startsWith("/home") && activeIndexRef.current !== 1) {
      setActiveIndex(1);
    } else if (
      pathname.startsWith("/NationalProxy") &&
      activeIndexRef.current !== 4
    ) {
      setActiveIndex(4);
    } else if (
      !pathname.startsWith("/home") &&
      !pathname.startsWith("/NationalProxy") &&
      activeIndexRef.current !== -1 &&
      !drawerVisible &&
      !walletSwitch &&
      !searchVisible
    ) {
      setActiveIndex(-1);
    }
  }, [
    pathname,
    drawerVisible,
    walletSwitch,
    searchVisible,
    lastWalletBarClicked,
  ]);

  // 键盘导航处理
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: BarIndex) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClickItem(index);
      }
    },
    [onClickItem]
  );

  // 渲染底部导航项 - 使用 useMemo 避免每次重新创建
  const barItems = useMemo(
    () =>
      items.map((item, key) => {
        const index = key as BarIndex;
        const isActive = activeIndex === index;

        return (
          <div
            key={key}
            className={cs(styles["bottombar-item"], {
              [styles.active]: isActive,
            })}
            onClick={() => onClickItem(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            role="button"
            aria-label={item.title}
            aria-pressed={isActive}
            tabIndex={0}
            data-testid={`bottombar-item-${key}`}
            data-bar-index={key}
          >
            <div
              className={cs(
                item.middleRadius
                  ? styles["bottombar-middle"]
                  : styles["bottombar-item-inner"]
              )}
            >
              <div
                className={cs(
                  item.middleRadius
                    ? styles["bottombar-middle-view"]
                    : styles["bottombar-item-icon"]
                )}
              >
                {typeof item.icon === "function" ? item.icon(isActive) : item.icon}
              </div>
              <div
                className={cs(
                  item.middleRadius
                    ? styles["bottombar-middle-title"]
                    : styles["bottombar-item-title"]
                )}
              >
                {item.title}
              </div>
            </div>
          </div>
        );
      }),
    [items, activeIndex, onClickItem, handleKeyDown]
  );

  return (
    <nav
      className={cs(styles["bottombar"])}
      data-app-bottombar="true"
      role="navigation"
      aria-label="Bottom navigation"
    >
      <div className={cs(styles["bottombar-inner"])}>{barItems}</div>
    </nav>
  );
}

// 使用React.memo包裹整个组件，避免父组件重渲染导致的不必要渲染
export default React.memo(Bottombar);
